// The full-width search card on the admin/staff/partner Search page: a pill search field, a row of
// labeled dropdown filters, and one chip per active measurable with a popover editor. Controlled: the
// page owns `filters`, `searchTerm` and `activeStatIds`; this only renders and edits them. Positions and
// measurables come from the sport catalog (the page passes positions; STAT_MENU / STAT_PRESETS for the rest).
import React, { useEffect, useRef, useState } from 'react';
import VerifiedFlame from '../VerifiedFlame';
import { POSITIONS as FALLBACK_POSITIONS, US_STATES, STAT_MENU, STAT_PRESETS } from '../../lib/playerSearchCatalog';

// Class, position and state have their own dropdowns, so they are never measurable chips.
const FIXED = ['graduation_year', 'position', 'state'];
const THIS_YEAR = new Date().getFullYear();
export const CLASS_YEARS = [0, 1, 2, 3].map((i) => String(THIS_YEAR + i));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const findDef = (id) => {
  for (const g of STAT_MENU) {
    const s = g.stats.find((x) => x.id === id);
    if (s) return s;
  }
  return null;
};

// "Dallas Combine · Jun 2026". Read month/year off the ISO string itself so a camp's wall-clock date
// never shifts a month in the viewer's timezone.
export const campLabel = (c) => {
  const m = /^(\d{4})-(\d{2})/.exec(c.date || '');
  return m ? `${c.name} · ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : c.name;
};

// The chip's value text: "any", "≥ min", "≤ max" or "min–max" for a range; the option label for a select.
export const chipValue = (def, filters) => {
  if (def.type === 'range') {
    const lo = filters[def.minKey];
    const hi = filters[def.maxKey];
    if (lo && hi) return `${lo}–${hi}`;
    if (lo) return `≥ ${lo}`;
    if (hi) return `≤ ${hi}`;
    return 'any';
  }
  const v = filters[def.filterKey];
  if (!v) return 'any';
  const opt = (def.options || []).find((o) => String(o.value) === String(v));
  return opt ? opt.label : String(v);
};

const isSet = (def, filters) => (def.type === 'range'
  ? !!(filters[def.minKey] || filters[def.maxKey])
  : !!filters[def.filterKey]);

const SearchIcon = () => (
  <svg className="srch-q__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="M16 16L20 20" />
  </svg>
);
const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

function MeasurablePopover({ def, filters, setFilters, onRemove, onDone }) {
  const set = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const preset = def.type === 'range' ? STAT_PRESETS[def.id] : null;
  return (
    <div className="srch-pop" role="dialog" aria-label={`${def.label} filter`}>
      <div className="srch-pop__title">{def.label}</div>
      {preset && (
        <div className="srch-pop__presets">
          {preset.values.map((v) => {
            const target = preset.key === 'min' ? def.minKey : def.maxKey;
            const other = preset.key === 'min' ? def.maxKey : def.minKey;
            const on = filters[target] === String(v);
            return (
              <button type="button" key={v} className={`srch-chip ${on ? 'srch-chip--on' : ''}`} aria-pressed={on}
                onClick={() => setFilters((prev) => ({ ...prev, [target]: on ? '' : String(v), ...(on ? {} : { [other]: '' }) }))}>
                {preset.fmt(v)}
              </button>
            );
          })}
        </div>
      )}
      {def.type === 'range' ? (
        <div className="srch-pop__range">
          <input className="srch-pop__num" type="number" placeholder="min" step={def.step || undefined}
            aria-label={`${def.label} minimum`} value={filters[def.minKey]} onChange={(e) => set(def.minKey, e.target.value)} />
          <span className="srch-pop__to">to</span>
          <input className="srch-pop__num" type="number" placeholder="max" step={def.step || undefined}
            aria-label={`${def.label} maximum`} value={filters[def.maxKey]} onChange={(e) => set(def.maxKey, e.target.value)} />
        </div>
      ) : (
        <div className="srch-pop__presets">
          {(def.options || []).map((o) => {
            const on = String(filters[def.filterKey]) === String(o.value);
            return (
              <button type="button" key={o.value} className={`srch-chip ${on ? 'srch-chip--on' : ''}`} aria-pressed={on}
                onClick={() => set(def.filterKey, on ? '' : String(o.value))}>{o.label}</button>
            );
          })}
        </div>
      )}
      <div className="srch-pop__foot">
        <button type="button" className="srch-link srch-link--danger" onClick={onRemove}>Remove</button>
        <button type="button" className="adm-btn adm-btn--dark adm-btn--sm" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

export default function SearchCard({
  searchTerm, onSearchTerm, onSubmit, filters, setFilters, positions,
  activeStatIds, onAddStat, onRemoveStat, campOptions, hasActiveFilters, onSaveNeed, onClear,
}) {
  // Position names come from the sport catalog (the page passes them); the football constants are only a
  // fallback for when the catalog has not loaded.
  const POSITIONS = positions && positions.length ? positions : FALLBACK_POSITIONS;
  const [editing, setEditing] = useState(null);   // stat id whose popover is open
  const [addOpen, setAddOpen] = useState(false);
  const [addFlip, setAddFlip] = useState(false); // anchor the add menu right when it would run off-screen
  const openRef = useRef(null);                   // wrapper of whichever popover is open

  // Any click outside the open popover closes it. (The Saved needs menu closes itself the same way, so
  // at most one of the three is ever open.)
  useEffect(() => {
    if (!editing && !addOpen) return undefined;
    const away = (e) => {
      if (openRef.current && !openRef.current.contains(e.target)) { setEditing(null); setAddOpen(false); }
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [editing, addOpen]);

  const set = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const chips = activeStatIds.filter((id) => !FIXED.includes(id)).map(findDef).filter(Boolean);
  const groups = STAT_MENU.map((g) => ({ ...g, stats: g.stats.filter((s) => !FIXED.includes(s.id)) }))
    .filter((g) => g.stats.length > 0);

  const add = (id) => { onAddStat(id); setAddOpen(false); setEditing(id); };
  const remove = (id) => { onRemoveStat(id); setEditing(null); };
  const toggle = (key) => setFilters((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="adm__card srch-box" role="search" aria-label="Filters">
      <div className="srch-q">
        <SearchIcon />
        <input className="srch-q__input" type="search" placeholder="Search athletes by name, school, or email"
          aria-label="Search athletes by name, school, or email" value={searchTerm}
          onChange={(e) => onSearchTerm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }} />
      </div>

      <div className="srch-filters">
        <label className="adm-field srch-field srch-field--class">
          <span className="adm-field__label">Class</span>
          <select className="adm-select" aria-label="Class" value={filters.graduation_year}
            onChange={(e) => set('graduation_year', e.target.value)}>
            <option value="">Any class</option>
            {CLASS_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="adm-field srch-field srch-field--position">
          <span className="adm-field__label">Position</span>
          <select className="adm-select" aria-label="Position" value={filters.position}
            onChange={(e) => set('position', e.target.value)}>
            <option value="">Any position</option>
            {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="adm-field srch-field srch-field--state">
          <span className="adm-field__label">State</span>
          <select className="adm-select" aria-label="State" value={filters.state} onChange={(e) => set('state', e.target.value)}>
            <option value="">Any state</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="adm-field srch-field srch-field--camp">
          <span className="adm-field__label">Camp</span>
          <select className="adm-select" aria-label="Filter by camp" value={filters.camp_id}
            onChange={(e) => set('camp_id', e.target.value)}>
            <option value="">Any camp</option>
            {campOptions.map((c) => <option key={c.id} value={c.id}>{campLabel(c)}</option>)}
          </select>
        </label>
        <div className="adm-field">
          <span className="adm-field__label" id="srch-only">Only</span>
          <div className="srch-only" role="group" aria-labelledby="srch-only">
            <button type="button" className={`srch-chip srch-toggle ${filters.verified_only ? 'srch-chip--on' : ''}`}
              aria-pressed={!!filters.verified_only} aria-label="Verified only" onClick={() => toggle('verified_only')}>
              <VerifiedFlame size={14} title="" /><span>Verified</span>
            </button>
            <button type="button" className={`srch-chip srch-toggle ${filters.has_photo ? 'srch-chip--on' : ''}`}
              aria-pressed={!!filters.has_photo} onClick={() => toggle('has_photo')}>Has photo</button>
          </div>
        </div>
      </div>

      <div className="srch-measures">
        <span className="adm-field__label srch-measures__label">Measurables</span>
        {chips.map((def) => {
          const on = isSet(def, filters);
          const open = editing === def.id;
          return (
            <div className="srch-anchor" key={def.id} ref={open ? openRef : undefined}>
              <span className={`srch-mchip ${on ? 'srch-mchip--on' : ''}`}>
                <button type="button" className="srch-mchip__body" aria-expanded={open} aria-haspopup="dialog"
                  onClick={() => { setAddOpen(false); setEditing(open ? null : def.id); }}>
                  <span className="srch-mchip__label">{def.label}</span>{chipValue(def, filters)}
                </button>
                <button type="button" className="srch-mchip__x" aria-label={`Remove ${def.label} filter`}
                  onClick={() => remove(def.id)}><XIcon /></button>
              </span>
              {open && (
                <MeasurablePopover def={def} filters={filters} setFilters={setFilters}
                  onRemove={() => remove(def.id)} onDone={() => setEditing(null)} />
              )}
            </div>
          );
        })}
        <div className="srch-anchor" ref={addOpen ? openRef : undefined}>
          <button type="button" className="srch-addm" aria-haspopup="menu" aria-expanded={addOpen}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setAddFlip(r.left + 520 > window.innerWidth - 16);
              setEditing(null); setAddOpen((v) => !v);
            }}>
            <PlusIcon />Add measurable
          </button>
          {addOpen && (
            <div className={`srch-addmenu ${addFlip ? 'srch-addmenu--right' : ''}`} role="menu" aria-label="Add measurable">
              {groups.map((g) => (
                <div className="srch-addmenu__group" key={g.group}>
                  <div className="srch-addmenu__head">{g.group}</div>
                  {g.stats.map((s) => {
                    const used = activeStatIds.includes(s.id);
                    return (
                      <button type="button" role="menuitem" key={s.id} className="srch-addmenu__item" disabled={used}
                        onClick={() => add(s.id)}>{s.label}</button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="srch-measures__end">
          {hasActiveFilters && <button type="button" className="srch-link" onClick={onClear}>Clear all</button>}
          <button type="button" className="adm-btn adm-btn--primary" disabled={!hasActiveFilters} onClick={onSaveNeed}
            title={hasActiveFilters ? 'Save these filters as a need' : 'Add a filter first'}>Save as need</button>
        </div>
      </div>
    </div>
  );
}
